// Primary 6 English Learning Data - Part 2 (Topics 11-18)
// Based on Primary English Learning P6 Topics.txt

export const p6DataPart2 = {
  topics: [
    {
      id: 'p6-topic-11',
      title: 'Essay Writing',
      description: 'Learning to write different types of essays',
      purpose: 'To teach students how to write different types of essays including descriptive, narrative, expository, and persuasive essays with proper structure and organization.',
      questions: [
        {
          question: 'What is the purpose of a descriptive essay?',
          answer: 'to create vivid images',
          explanation: 'A descriptive essay aims to create vivid images in the reader\'s mind using sensory details.'
        },
        {
          question: 'What is the purpose of a narrative essay?',
          answer: 'to tell a story',
          explanation: 'A narrative essay aims to tell a story with characters, plot, and setting.'
        },
        {
          question: 'What is the purpose of an expository essay?',
          answer: 'to explain information',
          explanation: 'An expository essay aims to explain information, ideas, or concepts clearly and logically.'
        },
        {
          question: 'What is the purpose of a persuasive essay?',
          answer: 'to convince the reader',
          explanation: 'A persuasive essay aims to convince the reader to accept the writer\'s point of view.'
        },
        {
          question: 'What should you include in an essay introduction?',
          answer: 'hook and thesis',
          explanation: 'Include a hook to grab attention and a thesis statement in the introduction.'
        },
        {
          question: 'What should you include in essay body paragraphs?',
          answer: 'topic sentence and evidence',
          explanation: 'Include a topic sentence and supporting evidence in each body paragraph.'
        },
        {
          question: 'What should you include in an essay conclusion?',
          answer: 'summary and restatement',
          explanation: 'Include a summary of main points and restatement of the thesis in the conclusion.'
        },
        {
          question: 'What should you use to connect paragraphs?',
          answer: 'transitions',
          explanation: 'Use transitions to connect paragraphs and help the essay flow smoothly.'
        },
        {
          question: 'What should you avoid in essay writing?',
          answer: 'repetition',
          explanation: 'Avoid repetition in essay writing - use varied vocabulary and sentence structure.'
        },
        {
          question: 'What should you do before writing an essay?',
          answer: 'plan and outline',
          explanation: 'Plan and create an outline before writing an essay to organize your ideas.'
        }
      ]
    },
    {
      id: 'p6-topic-12',
      title: 'Research Skills',
      description: 'Learning to conduct research and use sources effectively',
      purpose: 'To teach students how to conduct research, evaluate sources, and use information effectively in academic writing.',
      questions: [
        {
          question: 'What should you do first when starting research?',
          answer: 'identify your topic',
          explanation: 'First identify your research topic to focus your search and gather relevant information.'
        },
        {
          question: 'What should you look for when evaluating sources?',
          answer: 'credibility and relevance',
          explanation: 'Look for credibility and relevance when evaluating sources to ensure quality information.'
        },
        {
          question: 'What should you do when taking notes from sources?',
          answer: 'paraphrase and cite',
          explanation: 'Paraphrase information in your own words and cite sources to avoid plagiarism.'
        },
        {
          question: 'What should you include in a bibliography?',
          answer: 'complete source information',
          explanation: 'Include complete source information in a bibliography to allow others to find your sources.'
        },
        {
          question: 'What should you avoid when conducting research?',
          answer: 'plagiarism',
          explanation: 'Avoid plagiarism by properly citing sources and using your own words.'
        },
        {
          question: 'What should you do with conflicting information?',
          answer: 'evaluate and compare',
          explanation: 'Evaluate and compare conflicting information to determine which is more reliable.'
        },
        {
          question: 'What should you include in research notes?',
          answer: 'key points and page numbers',
          explanation: 'Include key points and page numbers in research notes for easy reference.'
        },
        {
          question: 'What should you do before using a source?',
          answer: 'verify its reliability',
          explanation: 'Verify the reliability of a source before using it in your research.'
        },
        {
          question: 'What should you include in a research outline?',
          answer: 'main topics and subtopics',
          explanation: 'Include main topics and subtopics in a research outline to organize your information.'
        },
        {
          question: 'What should you do when finding too much information?',
          answer: 'narrow your focus',
          explanation: 'Narrow your focus when finding too much information to stay within your scope.'
        }
      ]
    },
    {
      id: 'p6-topic-13',
      title: 'Advanced Reading Comprehension',
      description: 'Learning to understand and analyze complex texts',
      purpose: 'To teach students how to read and comprehend complex texts, identify main ideas, and analyze author\'s purpose and techniques.',
      questions: [
        {
          question: 'What should you do first when reading a complex text?',
          answer: 'skim for main ideas',
          explanation: 'First skim the text for main ideas to get an overview of the content.'
        },
        {
          question: 'What should you look for when identifying the main idea?',
          answer: 'repeated concepts',
          explanation: 'Look for repeated concepts when identifying the main idea of a text.'
        },
        {
          question: 'What should you do when you encounter unfamiliar words?',
          answer: 'use context clues',
          explanation: 'Use context clues to understand unfamiliar words when reading.'
        },
        {
          question: 'What should you look for when analyzing author\'s purpose?',
          answer: 'tone and word choice',
          explanation: 'Look for tone and word choice when analyzing the author\'s purpose.'
        },
        {
          question: 'What should you do when reading for details?',
          answer: 'read slowly and carefully',
          explanation: 'Read slowly and carefully when looking for specific details in a text.'
        },
        {
          question: 'What should you look for when identifying text structure?',
          answer: 'organizational patterns',
          explanation: 'Look for organizational patterns when identifying text structure.'
        },
        {
          question: 'What should you do when making inferences?',
          answer: 'use evidence from text',
          explanation: 'Use evidence from the text when making inferences about meaning.'
        },
        {
          question: 'What should you look for when analyzing arguments?',
          answer: 'evidence and reasoning',
          explanation: 'Look for evidence and reasoning when analyzing arguments in a text.'
        },
        {
          question: 'What should you do when reading different text types?',
          answer: 'adjust reading strategy',
          explanation: 'Adjust your reading strategy when reading different types of texts.'
        },
        {
          question: 'What should you do after reading a complex text?',
          answer: 'summarize main points',
          explanation: 'Summarize the main points after reading a complex text to ensure understanding.'
        }
      ]
    },
    {
      id: 'p6-topic-14',
      title: 'Advanced Speaking Skills',
      description: 'Learning to speak effectively in different contexts',
      purpose: 'To teach students how to speak effectively in different contexts including presentations, discussions, and formal conversations.',
      questions: [
        {
          question: 'What should you do to prepare for a speech?',
          answer: 'practice and rehearse',
          explanation: 'Practice and rehearse your speech to improve delivery and confidence.'
        },
        {
          question: 'What should you do to engage your audience?',
          answer: 'use eye contact',
          explanation: 'Use eye contact to engage your audience and show confidence.'
        },
        {
          question: 'What should you do when speaking in a group discussion?',
          answer: 'listen actively',
          explanation: 'Listen actively when participating in a group discussion to understand others\' views.'
        },
        {
          question: 'What should you avoid when giving a presentation?',
          answer: 'reading from notes',
          explanation: 'Avoid reading from notes when giving a presentation - speak naturally.'
        },
        {
          question: 'What should you do to improve pronunciation?',
          answer: 'practice regularly',
          explanation: 'Practice regularly to improve pronunciation and speaking clarity.'
        },
        {
          question: 'What should you do when speaking formally?',
          answer: 'use appropriate language',
          explanation: 'Use appropriate language when speaking formally to show respect and professionalism.'
        },
        {
          question: 'What should you do to show confidence when speaking?',
          answer: 'stand tall and speak clearly',
          explanation: 'Stand tall and speak clearly to show confidence when speaking.'
        },
        {
          question: 'What should you do when asking questions?',
          answer: 'be specific and clear',
          explanation: 'Be specific and clear when asking questions to get helpful responses.'
        },
        {
          question: 'What should you do when responding to others?',
          answer: 'acknowledge their points',
          explanation: 'Acknowledge others\' points when responding to show you understand their views.'
        },
        {
          question: 'What should you do to end a conversation politely?',
          answer: 'thank and summarize',
          explanation: 'Thank the person and summarize key points when ending a conversation politely.'
        }
      ]
    },
    {
      id: 'p6-topic-15',
      title: 'Academic Language',
      description: 'Learning to use academic language and formal expressions',
      purpose: 'To teach students how to use academic language, formal expressions, and sophisticated vocabulary appropriate for academic contexts.',
      questions: [
        {
          question: 'What should you use instead of "I think" in academic writing?',
          answer: 'it appears that',
          explanation: 'Use "it appears that" instead of "I think" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "big" in academic writing?',
          answer: 'significant',
          explanation: 'Use "significant" instead of "big" in academic writing for more precise language.'
        },
        {
          question: 'What should you use instead of "good" in academic writing?',
          answer: 'effective',
          explanation: 'Use "effective" instead of "good" in academic writing for more specific description.'
        },
        {
          question: 'What should you use instead of "bad" in academic writing?',
          answer: 'problematic',
          explanation: 'Use "problematic" instead of "bad" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "show" in academic writing?',
          answer: 'demonstrate',
          explanation: 'Use "demonstrate" instead of "show" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "look at" in academic writing?',
          answer: 'examine',
          explanation: 'Use "examine" instead of "look at" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "find out" in academic writing?',
          answer: 'discover',
          explanation: 'Use "discover" instead of "find out" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "get" in academic writing?',
          answer: 'obtain',
          explanation: 'Use "obtain" instead of "get" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "make" in academic writing?',
          answer: 'create',
          explanation: 'Use "create" instead of "make" in academic writing for more formal expression.'
        },
        {
          question: 'What should you use instead of "say" in academic writing?',
          answer: 'state',
          explanation: 'Use "state" instead of "say" in academic writing for more formal expression.'
        }
      ]
    },
    {
      id: 'p6-topic-16',
      title: 'Critical Thinking',
      description: 'Learning to think critically and evaluate information',
      purpose: 'To teach students how to think critically, evaluate information, and develop logical reasoning skills.',
      questions: [
        {
          question: 'What should you do when evaluating an argument?',
          answer: 'examine evidence',
          explanation: 'Examine evidence when evaluating an argument to determine its strength.'
        },
        {
          question: 'What should you look for when identifying bias?',
          answer: 'one-sided information',
          explanation: 'Look for one-sided information when identifying bias in sources.'
        },
        {
          question: 'What should you do when making a decision?',
          answer: 'consider alternatives',
          explanation: 'Consider alternatives when making a decision to ensure the best choice.'
        },
        {
          question: 'What should you look for when analyzing logic?',
          answer: 'cause and effect',
          explanation: 'Look for cause and effect relationships when analyzing logic in arguments.'
        },
        {
          question: 'What should you do when solving a problem?',
          answer: 'break it down',
          explanation: 'Break down a problem into smaller parts when solving it systematically.'
        },
        {
          question: 'What should you look for when evaluating sources?',
          answer: 'authority and expertise',
          explanation: 'Look for authority and expertise when evaluating the credibility of sources.'
        },
        {
          question: 'What should you do when forming an opinion?',
          answer: 'gather information',
          explanation: 'Gather information from multiple sources when forming an opinion.'
        },
        {
          question: 'What should you look for when identifying assumptions?',
          answer: 'unstated beliefs',
          explanation: 'Look for unstated beliefs when identifying assumptions in arguments.'
        },
        {
          question: 'What should you do when evaluating conclusions?',
          answer: 'check supporting evidence',
          explanation: 'Check supporting evidence when evaluating whether conclusions are justified.'
        },
        {
          question: 'What should you look for when analyzing reasoning?',
          answer: 'logical connections',
          explanation: 'Look for logical connections when analyzing the reasoning in arguments.'
        }
      ]
    },
    {
      id: 'p6-topic-17',
      title: 'Study Skills',
      description: 'Learning effective study techniques and strategies',
      purpose: 'To teach students effective study techniques, time management, and learning strategies for academic success.',
      questions: [
        {
          question: 'What should you do to organize your study time?',
          answer: 'create a schedule',
          explanation: 'Create a schedule to organize your study time effectively.'
        },
        {
          question: 'What should you do when taking notes?',
          answer: 'use your own words',
          explanation: 'Use your own words when taking notes to ensure understanding.'
        },
        {
          question: 'What should you do to remember information better?',
          answer: 'review regularly',
          explanation: 'Review information regularly to remember it better over time.'
        },
        {
          question: 'What should you do when studying for a test?',
          answer: 'practice with questions',
          explanation: 'Practice with questions when studying for a test to prepare effectively.'
        },
        {
          question: 'What should you do to understand difficult concepts?',
          answer: 'break them down',
          explanation: 'Break down difficult concepts into smaller parts to understand them better.'
        },
        {
          question: 'What should you do when reading textbooks?',
          answer: 'take notes',
          explanation: 'Take notes when reading textbooks to retain important information.'
        },
        {
          question: 'What should you do to stay focused while studying?',
          answer: 'eliminate distractions',
          explanation: 'Eliminate distractions to stay focused while studying.'
        },
        {
          question: 'What should you do when you don\'t understand something?',
          answer: 'ask for help',
          explanation: 'Ask for help when you don\'t understand something rather than struggling alone.'
        },
        {
          question: 'What should you do to prepare for class?',
          answer: 'read ahead',
          explanation: 'Read ahead to prepare for class and understand the material better.'
        },
        {
          question: 'What should you do after studying?',
          answer: 'test yourself',
          explanation: 'Test yourself after studying to check your understanding and retention.'
        }
      ]
    },
    {
      id: 'p6-topic-18',
      title: 'Test Preparation',
      description: 'Learning strategies for test preparation and exam success',
      purpose: 'To teach students effective strategies for preparing for tests, managing exam stress, and achieving academic success.',
      questions: [
        {
          question: 'What should you do first when preparing for a test?',
          answer: 'review the material',
          explanation: 'First review the material when preparing for a test to refresh your memory.'
        },
        {
          question: 'What should you do to manage test anxiety?',
          answer: 'practice relaxation techniques',
          explanation: 'Practice relaxation techniques to manage test anxiety and stay calm.'
        },
        {
          question: 'What should you do the night before a test?',
          answer: 'get enough sleep',
          explanation: 'Get enough sleep the night before a test to ensure mental clarity.'
        },
        {
          question: 'What should you do when taking a test?',
          answer: 'read instructions carefully',
          explanation: 'Read instructions carefully when taking a test to avoid mistakes.'
        },
        {
          question: 'What should you do if you don\'t know an answer?',
          answer: 'skip and return later',
          explanation: 'Skip difficult questions and return to them later to manage time effectively.'
        },
        {
          question: 'What should you do to check your answers?',
          answer: 'review your work',
          explanation: 'Review your work to check for mistakes before submitting the test.'
        },
        {
          question: 'What should you do to prepare for essay questions?',
          answer: 'practice writing',
          explanation: 'Practice writing to prepare for essay questions on tests.'
        },
        {
          question: 'What should you do to study effectively?',
          answer: 'use active learning',
          explanation: 'Use active learning techniques to study effectively and retain information.'
        },
        {
          question: 'What should you do when you finish early?',
          answer: 'check your answers',
          explanation: 'Check your answers when you finish early to ensure accuracy.'
        },
        {
          question: 'What should you do after receiving test results?',
          answer: 'review mistakes',
          explanation: 'Review mistakes after receiving test results to learn from them.'
        }
      ]
    }
  ]
}; 